/**
 * SMS Usage Gauge
 *
 * Shows current monthly SMS usage with color-coded progress bar.
 * - Active subscribed orgs: monthly usage out of (limit + topup credits)
 * - Trial orgs: trial-period lifetime usage out of 10
 * - Owner/signature accounts: informational gauge only (never blocked)
 *
 * Banner colors:
 *  - <60% green (calm)
 *  - 60-80% amber (heads-up)
 *  - 80-100% orange (warning)
 *  - >=100% red (blocked)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle, MessageCircle, RefreshCw, Crown, ShoppingCart } from "lucide-react";
import { format, endOfMonth } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL;

const TOPUP_BUNDLES = [
  { messages: 250, price: "$9.99", label: "250 messages" },
  { messages: 500, price: "$17.99", label: "500 messages" },
];

export const SmsUsageGauge = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/sms/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsage(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load SMS usage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const buyBundle = async (messages) => {
    setBuying(true);
    setBuyError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/api/sms/topup-checkout`,
        { messages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.payment_link_url) {
        window.open(res.data.payment_link_url, "_blank", "noopener,noreferrer");
        setPickerOpen(false);
      } else {
        setBuyError("Could not open payment page. Please try again.");
      }
    } catch (err) {
      setBuyError(err.response?.data?.detail || "Purchase failed. Please try again.");
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="sms-usage-gauge-loading" className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading messaging usage…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="sms-usage-gauge-error" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" /> {error}
      </div>
    );
  }

  if (!usage) return null;

  const isOwner = usage.plan === "signature";
  const isTrial = usage.status === "trial";

  // Decide which counter to display
  const used = isTrial ? usage.trialSmsSent : usage.monthlyUsed;
  const limit = isTrial ? usage.trialSmsLimit : usage.totalAllowance;
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;

  // Color thresholds
  let barColor = "bg-emerald-500";
  let labelColor = "text-emerald-700";
  let bg = "bg-emerald-50";
  let border = "border-emerald-200";
  let icon = <MessageCircle className="w-5 h-5 text-emerald-600" />;
  let banner = null;

  if (isOwner) {
    barColor = "bg-amber-400";
    labelColor = "text-amber-800";
    bg = "bg-amber-50";
    border = "border-amber-300";
    icon = <Crown className="w-5 h-5 text-amber-600" />;
  } else if (pct >= 100) {
    barColor = "bg-rose-600";
    labelColor = "text-rose-700";
    bg = "bg-rose-50";
    border = "border-rose-300";
    icon = <AlertCircle className="w-5 h-5 text-rose-600" />;
    banner = (
      <p className="text-xs font-semibold text-rose-800 mt-2">
        {isTrial
          ? "Trial messaging limit reached. Subscribe to continue."
          : "Monthly messaging limit reached. Buy more messages to keep sending today."}
      </p>
    );
  } else if (pct >= 80) {
    barColor = "bg-orange-500";
    labelColor = "text-orange-700";
    bg = "bg-orange-50";
    border = "border-orange-300";
    icon = <AlertCircle className="w-5 h-5 text-orange-600" />;
    banner = (
      <p className="text-xs text-orange-800 mt-2">
        Approaching your monthly cap. Top-up bundles are available if you need more this month.
      </p>
    );
  } else if (pct >= 60) {
    barColor = "bg-amber-500";
    labelColor = "text-amber-800";
    bg = "bg-amber-50";
    border = "border-amber-300";
  }

  // Reset label
  const today = new Date();
  const resetDate = endOfMonth(today);
  const resetLabel = format(resetDate, "MMM d, yyyy");

  return (
    <div
      data-testid="sms-usage-gauge"
      className={`rounded-xl border-2 ${border} ${bg} p-4`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <p className={`font-bold ${labelColor}`}>
            {isOwner
              ? "Messaging Usage (Signature Tier — unlimited)"
              : isTrial
              ? "Trial Messaging Usage"
              : "Monthly Messaging Usage"}
          </p>
        </div>
        <div className={`text-sm font-semibold ${labelColor}`}>
          <span data-testid="sms-usage-gauge-used">{used}</span>
          <span className="text-slate-500"> of </span>
          <span data-testid="sms-usage-gauge-limit">{limit}</span>
          <span className="text-slate-500"> messages</span>
          {!isTrial && usage.topupCredits > 0 && (
            <span className="ml-2 inline-block rounded-full bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5">
              +{usage.topupCredits} top-up
            </span>
          )}
        </div>
      </div>

      <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${pct}%` }}
          data-testid="sms-usage-gauge-bar"
        />
      </div>

      <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
        <span>
          {isTrial
            ? "Resets when you subscribe"
            : `Resets ${resetLabel}`}
        </span>
        <button
          onClick={load}
          className="underline hover:text-slate-700"
          data-testid="sms-usage-gauge-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {banner}

      {!isTrial && !isOwner && pct >= 80 && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          {!pickerOpen ? (
            <button
              onClick={() => setPickerOpen(true)}
              data-testid="sms-topup-open-picker-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-3 py-2 text-sm font-semibold hover:bg-slate-800 transition"
            >
              <ShoppingCart className="w-4 h-4" />
              Buy More Messages
            </button>
          ) : (
            <div className="space-y-2" data-testid="sms-topup-picker">
              <p className="text-xs font-semibold text-slate-700">Choose a bundle:</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {TOPUP_BUNDLES.map((b) => (
                  <button
                    key={b.messages}
                    disabled={buying}
                    onClick={() => buyBundle(b.messages)}
                    data-testid={`sms-topup-buy-${b.messages}-btn`}
                    className="flex items-center justify-between rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm hover:border-slate-900 hover:bg-slate-50 disabled:opacity-50 transition"
                  >
                    <span className="font-semibold text-slate-800">{b.label}</span>
                    <span className="text-slate-600">{b.price}</span>
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={() => { setPickerOpen(false); setBuyError(null); }}
                  className="text-xs text-slate-500 underline hover:text-slate-700"
                  data-testid="sms-topup-cancel-btn"
                >
                  Cancel
                </button>
                {buying && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3 animate-spin" /> Opening Square…
                  </span>
                )}
              </div>
              {buyError && (
                <p className="text-xs text-rose-700" data-testid="sms-topup-error">
                  {buyError}
                </p>
              )}
              <p className="text-[11px] text-slate-500 leading-snug">
                Top-up credits never expire — they roll over month to month until used.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmsUsageGauge;
